import InfoItem from "@/components/application/InfoItem";
import { DeleteRegistration } from "@/services/application.service";
import { safeFormatyyyyMMddDateTime } from "@/utils/formatDate";
import type { ApplicationItem } from "@/types/Application/application";
import { Button, Form, Grid, message, Modal, Typography } from "antd";
import { X } from "lucide-react";
import { useState } from "react";

interface DeleteApplicationModalProps {
  open: boolean;
  registrationId: string;
  name: string;
  selectedRow?: ApplicationItem | null;
  detailData?: Partial<ApplicationItem> | null;
  onClose: () => void;
  onBack: () => void;
  onConfirm: () => void;
  onSuccess?: () => void;
}

const { Title } = Typography;
const { useBreakpoint } = Grid;

const modalStyles = {
  container: { padding: 0 },
  header: { padding: 0, marginBottom: 0 },
  body: { padding: 24 },
  footer: { padding: "0 16px 16px" },
} as const;

export function DeleteApplicationModal({
  open,
  registrationId,
  name,
  selectedRow,
  detailData,
  onClose,
  onBack,
  onConfirm,
  onSuccess,
}: DeleteApplicationModalProps) {
  const screens = useBreakpoint();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    if (!registrationId) {
      message.error("Registration ID tidak dijumpai.");
      return;
    }

    try {
      setLoading(true);

      const values = await form.validateFields();

      await DeleteRegistration({
        registrationId,
        reason: values.reason ?? "",
      });

      onConfirm();
      form.resetFields();
      onSuccess?.();
    } catch (error) {
      console.error("Delete registration error:", error);

      message.error(
        error instanceof Error ? error.message : "Gagal memadam permohonan.",
      );
    } finally {
      setLoading(false);
    }
  };

  const modalItemTitle = (
    <>
      <div className="flex items-center justify-between p-4 sm:p-6 bg-[#E5F2FF] rounded-tl-2xl rounded-tr-2xl">
        <div className="flex items-center gap-4 sm:gap-6">
          <Title
            level={1}
            style={{ margin: 0, fontSize: screens.xs ? "22px" : "28px" }}
          >
            {detailData?.villageName ?? selectedRow?.villageName ?? "-"}
          </Title>
          <span className="text-sm sm:text-lg font-bold bg-white rounded-lg p-4 h-11 leading-none inline-flex items-center justify-center">
            {detailData?.registrationCode ?? selectedRow?.registrationCode ?? "-"}
          </span>
        </div>

        <div className="flex items-center gap-4 sm:gap-6">
          <span className="text-sm sm:text-lg capitalize font-bold border-[#000000] border rounded-lg p-4 h-11 leading-none inline-flex items-center justify-center">
            {detailData?.residencyStatus ?? selectedRow?.residencyStatus ?? "-"}
          </span>

          <div className="text-right">
            <span className="block text-xs font-semibold text-[#666] leading-none">
              Tarikh pendaftaran
            </span>
            <div className="text-sm sm:text-lg font-bold">
              {safeFormatyyyyMMddDateTime(
                detailData?.submittedOn ?? selectedRow?.submittedOn,
              ) ?? "-"}
            </div>
          </div>

          <button type="button" className="cursor-pointer" onClick={onClose}>
            <X size={28} aria-hidden="true" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <Modal
      title={modalItemTitle}
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      styles={modalStyles}
      width={1200}
      centered
    >
      <Title
        level={2}
        style={{
          textAlign: "center",
          fontSize: screens.xs ? 18 : 24,
          fontWeight: 700,
          marginBottom: 40,
        }}
      >
        Adakah anda pasti ingin <span className="text-[#FF4D4F]">memadam</span>{" "}
        permohonan ini?
      </Title>

      <div className="mb-8">
        <InfoItem label="Nama" value={name} />
      </div>

      <div className="grid grid-cols-2 gap-6 sm:gap-10 mt-8">
        <Button
          color="default"
          variant="solid"
          size="large"
          onClick={onBack}
          className="w-full"
        >
          Kembali
        </Button>

        <Button
          type="primary"
          size="large"
          style={{ backgroundColor: "#C00" }}
          className="w-full"
          onClick={handleConfirm}
          loading={loading}
        >
          Padam
        </Button>
      </div>
    </Modal>
  );
}
